import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchAutocomplete, type FacetCategory } from '../SearchAutocomplete';
import { useFacets } from '../../hooks/useFacets';
import { getApiBaseUrl } from '../../constants/environment';
import { catalogSearchHref } from '../../utils/catalogTagSearch';
import { trackSearch, tryTriggerSupportModal } from '../../utils/supportModalTracking';
import type { RiftboundCard } from '../../types';
import type { CardSuggestion } from '../CardSearchSuggestionRow';
import { useTabletHeaderSearch } from './HeaderSearchSlot';

const CardModal = lazy(() => import('../card/CardModal').then((m) => ({ default: m.CardModal })));

const FACET_PARAM: Record<FacetCategory, string> = {
  keyword: 'keyword',
  cardType: 'type',
  supertype: 'supertype',
  region: 'region',
  subtype: 'subtype',
  rarity: 'rarity',
  domain: 'domain',
  artist: 'artist',
};

/**
 * Card search for tablet pages that do not already filter a catalog.
 * Picking a card opens it here. A query or facet opens Cards with it applied.
 */
export function TabletHeaderCatalogSearch() {
  const tablet = useTabletHeaderSearch();
  if (!tablet) return null;
  return <TabletHeaderCatalogSearchField />;
}

function TabletHeaderCatalogSearchField() {
  const navigate = useNavigate();
  const facets = useFacets();
  const [term, setTerm] = useState('');
  const [card, setCard] = useState<RiftboundCard | null>(null);
  const [open, setOpen] = useState(false);
  const requestRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const openSearch = (query: Record<string, string>) => {
    const href = catalogSearchHref(query);
    if (!href) return;
    const q = query.q?.trim();
    if (q) {
      trackSearch(q);
      tryTriggerSupportModal();
    }
    navigate(href);
  };

  const openCard = (suggestion: CardSuggestion) => {
    const request = ++requestRef.current;
    void (async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/card?id=${encodeURIComponent(suggestion.id)}`);
        if (!res.ok) throw new Error('not found');
        const full = (await res.json()) as RiftboundCard;
        if (!mountedRef.current || request !== requestRef.current) return;
        if (!full?.id) throw new Error('not found');
        setCard(full);
        setOpen(true);
      } catch {
        if (!mountedRef.current || request !== requestRef.current) return;
        navigate(`/cards/${encodeURIComponent(suggestion.id)}`);
      }
    })();
  };

  return (
    <>
      <SearchAutocomplete
        value={term}
        onChange={setTerm}
        onCommit={(next) => {
          const q = next.trim();
          if (!q) return;
          openSearch({ q });
        }}
        onCommitCard={openCard}
        onCommitFacet={(category, value) => {
          openSearch({ [FACET_PARAM[category]]: value });
        }}
        facets={{
          keywords: facets.keywords,
          cardTypes: facets.cardTypes,
          supertypes: facets.supertypes,
          regions: facets.regions,
          subtypes: facets.subtypes,
          rarities: facets.rarities,
          artists: facets.artists,
        }}
      />
      {card ? (
        <Suspense fallback={null}>
          <CardModal card={card} open={open} onClose={() => setOpen(false)} />
        </Suspense>
      ) : null}
    </>
  );
}
